import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { TipoBadge } from "@/components/TipoBadge";
import { CursoFormDialog } from "@/components/forms/CursoFormDialog";
import { TurmaFormDialog } from "@/components/forms/TurmaFormDialog";
import { OficinaFormDialog } from "@/components/forms/OficinaFormDialog";
import {
  ArrowLeft, Loader2, Pencil, Plus, GraduationCap, Dumbbell, Users, ClipboardCheck, FolderKanban, BookOpen,
} from "lucide-react";

const STATUS_BADGE = {
  ativo: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
  planejado: "bg-sky-500/20 text-sky-300 border-sky-500/40",
  encerrado: "bg-slate-500/20 text-slate-300 border-slate-500/40",
};

export default function CursoDetalhe() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin } = useAuth();
  const backTo = location.pathname.startsWith("/instrucoes") ? "/instrucoes" : "/cursos";
  const backLabel = backTo === "/instrucoes" ? "Instruções" : "Cursos";
  const [curso, setCurso] = useState(null);
  const [turmas, setTurmas] = useState([]);
  const [oficinas, setOficinas] = useState([]);
  const [biblioteca, setBiblioteca] = useState([]);
  const [nAval, setNAval] = useState(0);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [turmaOpen, setTurmaOpen] = useState(false);
  const [oficinaOpen, setOficinaOpen] = useState(false);

  const load = useCallback(() => {
    setLoading(true); setErro(false);
    Promise.all([
      api.get(`/cursos/${id}`),
      api.get(`/turmas?curso_id=${id}`),
      api.get(`/oficinas?curso_id=${id}`),
      api.get(`/avaliacoes?curso_id=${id}`),
    ]).then(([c, t, o, a]) => {
      setCurso(c.data); setTurmas(t.data); setOficinas(o.data); setNAval(a.data.length);
      if (c.data?.tipo === "instrucao") {
        api.get("/biblioteca").then((r) => {
          const nome = c.data.nome;
          setBiblioteca((r.data || []).filter((b) => b.instrucao === nome || b.institucional));
        }).catch(() => setBiblioteca([]));
      } else { setBiblioteca([]); }
    }).catch(() => setErro(true)).finally(() => setLoading(false));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  if (erro || !curso) return (
    <div className="text-center py-20">
      <p className="text-muted-foreground">Registro não encontrado ou indisponível.</p>
      <button data-testid="voltar-btn" onClick={() => navigate(backTo)} className="mt-4 inline-flex items-center gap-1.5 text-sm text-primary hover:underline">
        <ArrowLeft className="h-4 w-4" /> Voltar para {backLabel}
      </button>
    </div>
  );

  const isInstrucao = curso.tipo === "instrucao";
  const canManage = isAdmin || (isInstrucao && curso.permite_aplicacoes_instrutor);

  return (
    <div data-testid="curso-detalhe">
      <button data-testid="voltar-btn" onClick={() => navigate(backTo)} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4">
        <ArrowLeft className="h-4 w-4" /> {backLabel}
      </button>
      <PageHeader title={curso.nome} subtitle={curso.descricao || (isInstrucao ? "Detalhes da instrução" : "Detalhes do curso")}>
        <div className="flex items-center gap-2">
          <TipoBadge tipo={curso.tipo} />
          {isAdmin && (
            <Button variant="secondary" data-testid="editar-curso-btn" onClick={() => setEditOpen(true)}><Pencil className="h-4 w-4 mr-2" />Editar</Button>
          )}
        </div>
      </PageHeader>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <Card className="p-4 bg-card border-border">
          <Badge className={STATUS_BADGE[curso.status] || "bg-secondary text-secondary-foreground border-border"}>{curso.status}</Badge>
          <div className="text-xs text-muted-foreground font-mono mt-2 space-y-0.5">
            {curso.periodo && <div>Período: {curso.periodo}</div>}
            {curso.carga_horaria && <div>{curso.carga_horaria}</div>}
            {curso.usa_tipologia_cec && <div className="text-primary">Tipologia CEC</div>}
            {curso.permite_aplicacoes_instrutor && <div className="text-primary">Aplicações do instrutor</div>}
          </div>
        </Card>
        <Stat icon={GraduationCap} label="Turmas" value={turmas.length} color="text-purple-400" />
        <Stat icon={Dumbbell} label="Oficinas" value={oficinas.length} color="text-emerald-400" />
        <Stat icon={ClipboardCheck} label="Avaliações" value={nAval} color="text-sky-400" />
      </div>

      <Section title={isInstrucao ? "Aplicações" : "Turmas"} icon={GraduationCap}
        action={canManage && <Button size="sm" variant="secondary" data-testid="add-turma-btn" onClick={() => setTurmaOpen(true)}><Plus className="h-4 w-4 mr-1" />{isInstrucao ? "Nova aplicação" : "Nova turma"}</Button>}>
        {turmas.length ? turmas.map((t) => (
          <Row key={t.id} testid={`turma-link-${t.id}`} onClick={() => navigate(`/turmas/${t.id}`)}
            title={t.nome} subtitle={`${t.status}${t.instituicao ? ` · ${t.instituicao}` : ""}`} icon={GraduationCap} />
        )) : <Empty>{isInstrucao ? "Nenhuma aplicação nesta instrução." : "Nenhuma turma neste curso."}</Empty>}
      </Section>

      <Section title={isInstrucao ? "Oficinas da Instrução" : "Oficinas"} icon={Dumbbell}
        action={canManage && <Button size="sm" variant="secondary" data-testid="add-oficina-btn" onClick={() => setOficinaOpen(true)}><Plus className="h-4 w-4 mr-1" />Nova oficina</Button>}>
        {oficinas.length ? oficinas.map((o) => (
          <Row key={o.id} testid={`oficina-link-${o.id}`} onClick={() => navigate(`/oficinas/${o.id}?back=${encodeURIComponent(location.pathname)}`)}
            title={o.nome} subtitle={`${(o.criterios || []).length} critérios${o.tipologia_cec_nome ? ` · CEC: ${o.tipologia_cec_nome}` : ""}`} icon={Dumbbell} />
        )) : <Empty>{isInstrucao ? "Nenhuma oficina nesta instrução." : "Nenhuma oficina neste curso."}</Empty>}
      </Section>

      {isInstrucao && (
        <Section title="Material / Biblioteca relacionada" icon={BookOpen}
          action={<Button size="sm" variant="outline" data-testid="ver-biblioteca-btn" onClick={() => navigate("/biblioteca")}><BookOpen className="h-4 w-4 mr-1" />Abrir Biblioteca</Button>}>
          {biblioteca.length ? biblioteca.map((b) => (
            <Row key={b.id} testid={`biblio-link-${b.id}`} onClick={() => navigate("/biblioteca")}
              title={b.titulo} subtitle={`${b.numero || "POP"}${b.categoria ? ` · ${b.categoria}` : ""}`} icon={BookOpen} />
          )) : <Empty>Nenhum material relacionado.</Empty>}
        </Section>
      )}

      <CursoFormDialog open={editOpen} onOpenChange={setEditOpen} curso={curso} onSaved={load} />
      <TurmaFormDialog open={turmaOpen} onOpenChange={setTurmaOpen} lockCursoId={id} onSaved={load} />
      <OficinaFormDialog open={oficinaOpen} onOpenChange={setOficinaOpen} lockCursoId={id} onSaved={load} />
    </div>
  );
}

function Stat({ icon: Icon, label, value, color }) {
  return (
    <Card className="p-4 bg-card border-border flex items-center gap-3">
      <div className="p-2 rounded-lg bg-secondary"><Icon className={`h-5 w-5 ${color}`} /></div>
      <div><div className="font-heading text-2xl font-bold">{value}</div><div className="text-xs text-muted-foreground">{label}</div></div>
    </Card>
  );
}
function Section({ title, icon: Icon, action, children }) {
  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2"><Icon className="h-5 w-5 text-primary" /><h2 className="font-heading text-xl font-bold uppercase tracking-tight">{title}</h2></div>
        {action}
      </div>
      <Card className="bg-card border-border divide-y divide-border">{children}</Card>
    </div>
  );
}
function Row({ title, subtitle, icon: Icon, onClick, testid }) {
  return (
    <button data-testid={testid} onClick={onClick} className="w-full flex items-center gap-3 px-4 py-3 hover:bg-secondary/50 transition-colors text-left">
      <div className="p-2 rounded-lg bg-secondary shrink-0"><Icon className="h-4 w-4 text-muted-foreground" /></div>
      <div className="min-w-0"><div className="font-medium truncate">{title}</div><div className="text-xs text-muted-foreground truncate">{subtitle}</div></div>
      <FolderKanban className="h-4 w-4 ml-auto text-muted-foreground/50 rotate-0" />
    </button>
  );
}
function Empty({ children }) {
  return <div className="px-4 py-8 text-center text-sm text-muted-foreground">{children}</div>;
}
